import * as React from 'react';
import { cn } from '@/lib/utils';

/*
 * shadcn/ui Card tuned to design/DESIGN.md: 8 px radius, hairline border, one hairline shadow; sections are
 * separated by a top border instead of the registry's padding + gap, because every Hangar card hosts a header, an
 * optional inline form and a list/table/empty state with their own gutters.
 */
function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card" className={cn('flex flex-col rounded-lg border bg-card text-card-foreground shadow-card', className)} {...props} />;
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-x-4 gap-y-0.5 px-4 pb-4 pt-4 has-data-[slot=card-action]:grid-cols-[minmax(0,1fr)_auto] sm:px-5 sm:pt-5',
        // Phones: a button action stacks under the text, so the header collapses to one column (see CardAction).
        'max-sm:has-[>[data-slot=card-action]>[data-slot=button]]:grid-cols-1',
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-title" className={cn('col-start-1 min-w-0 text-base font-semibold leading-tight tracking-tight', className)} {...props} />;
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-description" className={cn('col-start-1 text-[13px] text-muted-foreground', className)} {...props} />;
}

/** Header action. On phones an action that holds a button stacks under the title/description (the header wraps). */
function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-2 row-span-2 row-start-1 flex items-center gap-2 self-start justify-self-end',
        'max-sm:has-[>[data-slot=button]]:col-start-1 max-sm:has-[>[data-slot=button]]:row-span-1 max-sm:has-[>[data-slot=button]]:row-start-3 max-sm:has-[>[data-slot=button]]:mt-3 max-sm:has-[>[data-slot=button]]:justify-self-start',
        className,
      )}
      {...props}
    />
  );
}

/** Content section; pass `p-0 sm:p-0` (both breakpoints) when a list/table/empty state brings its own gutters. */
function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn('border-t p-4 sm:p-5', className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-footer" className={cn('flex items-center border-t px-4 py-3 sm:px-5', className)} {...props} />;
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };
